import type {IChangesObject, IComponentOptions} from 'angular';
import type {ActionResponse, Department, Loan, SearchParams} from 'typings/portaro.be.types';
import type {Location, Identified} from 'typings/portaro.be.types';

/**
 * @ngdoc module
 * @name portaro.types
 * @module types
 *
 * @description
 * Type definitions
 */


export interface FactoryFunction {
    (...rest): any;
    factoryName: string;
}

export interface Pageable {
    pageNumber: number;
    pageSize: number;
}

/**
 * @ngdoc type
 * @name Link
 * @module portaro.types
 *
 * @property {string} text Link text
 * @property {string} url Url of link
 * @property {string} [originParameterName]
 */
export interface Link {
    url: string;
    text: string;
    originParameterName?: string;
}

export interface RenewalRequest {
    state: string;
    text: string;
    success: boolean;
}

export enum DataDisplayerOptions {
    Edit,
    Delete,
}

export interface DataDisplayerSettings {
    options: DataDisplayerOptions[];
    loadAll: () => Promise<Identified<number | string>[]>;
    columnsSetting: ColumnsSetting[];
}

export interface ColumnsSetting {
    title: string;
    key: string;
    valueModifier: (value) => any;
}

export interface RenewableLoan extends Loan {
    renewalRequest: RenewalRequest;
}

export interface ViewableDepartment extends Department {
    parent: Department;
    locations: Location[];
}

export type TComponentBindings = '@' | '<' | '&' | '=' | '@?' | '<?' | '&?' | '=?';

export interface ComponentController<T> {
    prototype: [T] extends [never] ? any : T;
}

export interface Component<BINDINGS> extends Omit<IComponentOptions, 'bindings' | 'controller'> {
    bindings?: {
        [prop in keyof BINDINGS]: TComponentBindings;
    };

    controller?: ComponentController<BINDINGS>;
}

export type OnChangesObject<BINDINGS> = {
    [property in keyof BINDINGS]: IChangesObject<BINDINGS[property]>;
};

export interface OnChanges<BINDINGS> {
    $onChanges(onChangesObj: OnChangesObject<BINDINGS>): void;
}

export interface ModalWindowResult<FINISHED_RESPONSE extends ActionResponse = any> {
    finishedResponse: FINISHED_RESPONSE;
    isSuccessful: boolean;
    reopen: boolean;
}

export interface PayloadActionResponse<T> extends ActionResponse {
    payload?: T;
}

export type AnyObject = Record<string, unknown>; // replacement for {}, object, Object

export type EmptyObject = Record<string, never> // replacement for {} (empty)

export type StateChangeHandler = (params: SearchParams) => PromiseLike<void>;

export interface StateManager {
    register(stateChangeHandler: StateChangeHandler): (() => void);
    requestChangeState(newParams: SearchParams): Promise<void>;
    requestChangeStateParameter<T extends keyof SearchParams>(parameterName: T, newValue: SearchParams[T]): Promise<void>;
    isLoading(): boolean;
}

export interface RecordCollectionViewConfiguration {
    loansEnabled: boolean;
    exportsEnabled: boolean;
    exportLinks: Link[];
    printExportLinks: Link[];
}